import { usePageProps, withLayouts } from 'react-dx'
import { Trans, useLingui } from '@lingui/react/macro'

import RootLayout from '../_layout/layouts/RootLayout'
import AntdLayout from '../_layout/layouts/AntdLayout'
import SiteSpecificContextLayout from '../_layout/layouts/SiteSpecificContextLayout'
import { paddingHeaderHeightCls } from '../_layout/Header/constants'
import LayoutsDefaultLayout from '../_layout'
import MusicList from '../_components/MusicList'
import { PageStoreProvider } from '../_shared/formStore'
import { useJoinCreditsHintVisibility } from '../ai-music-generator/_blocks/GeneratorForm/components/GeneratorLyricsSmart/_hooks/useJoinCreditsHintVisibility'

import { createCoverStore } from './_store'
import CoverForm from './_blocks/CoverForm'

import type { MusicListActionRef } from '../_components/MusicList'
import type { InferGetServerSidePropsType } from 'next'
import type { TNextPagePro } from '@/types/global'

import { getServerSideData } from '@/helpers/server-data'
import { FluentFlex, FluentFlexRoot } from '@/components/FluentFlex'
import { getTadFeatureData } from '@/pages/tad.ai/_services/feature-service'
import { FeatureSeoPage } from '@/pages/tad.ai/_blocks/FeatureSeoPage'
import { useAppUIModal } from '@/contexts/AppUIModalContext'
import { useSessionInfo } from '@/contexts/AuthContext/hooks/sessionInfo'
import { useIsMounted } from '@/hooks/isMounted'


import { cls } from 'tagged-classnames-free';
import { useRef, useMemo } from 'react';
import { useGlobalAudioPlayer } from '@/hooks/audio/store';
import { useConfig } from '@/contexts/SiteContext/hooks';
const fluentFlexItemClassName = cls`flex w-full overflow-auto xl:flex-1`

export const Page: TNextPagePro<
  InferGetServerSidePropsType<typeof getServerSideProps>
> = (props) => {
  const {
    seoPageData: { hero },
  } = props
  const { t } = useLingui()
  const musicListRef = useRef<MusicListActionRef>(null)
  const { collapsed } = useGlobalAudioPlayer()

  const { signUp } = useAppUIModal()
  const { isSignIn } = useSessionInfo()
  const { isMounted } = useIsMounted()
  const { hasSignedInBefore } = useJoinCreditsHintVisibility(isSignIn)

  const showJoinCreditsHint = useMemo(() => {
    return isMounted && !isSignIn && !hasSignedInBefore
  }, [isMounted, isSignIn, hasSignedInBefore])

  return (
    <PageStoreProvider createStore={createCoverStore}>
      <FluentFlexRoot itemClassName={fluentFlexItemClassName}>
        <FluentFlex
          className={`min-h-screen flex-col xl:h-screen ${paddingHeaderHeightCls}`}
        >
          <div className='flex w-full items-center justify-between px-8 xl:w-[680px]'>
            <h1
              id='top'
              className='mb-3 text-xl font-bold md:mb-0 md:text-3xl md:leading-[64px]'
            >
              {hero?.h1 || <Trans>AI Cover Generator</Trans>}
            </h1>
            {showJoinCreditsHint && (
              <div className='flex justify-end pb-2'>
                <button
                  type='button'
                  className='before:i-cus--tad-member-gold after:i-com--right-arrow inline-flex size-fit items-center gap-1 rounded-[20px] bg-[#ffcc0029] px-3 py-1.5 text-center text-[rgb(255,235,184)] before:inline-block before:size-4 after:inline-block after:size-4 hover:bg-[#ffcc004d]'
                  onClick={() => {
                    signUp.show({ defaultShowSignUp: true })
                  }}
                >
                  {t`Join & Get 25 Free Credits`}
                </button>
              </div>
            )}
          </div>

          <FluentFlex
            className={cls`block flex-col gap-5 xl:flex-row ${!collapsed && 'pb-32 xl:pb-24'}`}
          >
            <CoverForm
              onCreated={() => {
                musicListRef.current?.refetchIfPage1(true)
              }}
            />
            <MusicList
              ref={musicListRef}
              className={`mx-auto h-full xl:max-w-none`}
            />
          </FluentFlex>
        </FluentFlex>
      </FluentFlexRoot>
    </PageStoreProvider>
  )
}

Page.noPageKey = true

const LayoutsPage = withLayouts(
  Page,
  [
    (props) => {
      const { seoPageData } =
        usePageProps<InferGetServerSidePropsType<typeof getServerSideProps>>()

      return (
        <LayoutsDefaultLayout
          {...props}
          main={{
            className: cls`bg-f-other-1`,
            includingHeader: true,
          }}
          sideBar={{
            className: '',
          }}
        >
          {props.children}
          <FeatureSeoPage pageData={seoPageData} />
        </LayoutsDefaultLayout>
      )
    },
    (props) => {
      const { seoPageData } =
        usePageProps<InferGetServerSidePropsType<typeof getServerSideProps>>()
      const { seoTitle } = useConfig()

      return (
        <RootLayout
          seo={{
            title: seoTitle(seoPageData.title),
            description: seoPageData.description,
          }}
          {...props}
        ></RootLayout>
      )
    },
    (props) => {
      return <SiteSpecificContextLayout {...props} />
    },
    AntdLayout,
  ],
  {
    propertiesHoist: ['noPageKey'],
  },
)

export default LayoutsPage

// eslint-disable-next-line react-refresh/only-export-components
export const getServerSideProps = getServerSideData({
  cacheControl: true,
})(async (hostInfo, ctx, helpers) => {
  const trpcHelpers = await helpers.getTrpcHelpers()
  const locale = ctx?.locale || 'en'
  const url = hostInfo.normalizeUrl().url.pathname

  const [, seoPageData] = await Promise.all([
    // SSR 预取 Style 标签树数据，客户端 useQuery 自动命中缓存
    trpcHelpers.preset.labelsTree.prefetch({ language: locale }),
    getTadFeatureData(url, locale),
  ])

  return {
    trpcState: trpcHelpers.dehydrate(),
    seoPageData,
  }
})
